import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtL, fmtDate } from "@/lib/format";
import { PrinterIcon, ArrowLeftIcon } from "@phosphor-icons/react";

export default function InvoicePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invoices/${id}/print-data`);
        setData(data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [id]);

  if (!data) return <div className="p-8 text-center text-gray-500">Cargando documento…</div>;
  const { invoice: inv, company, cliente } = data;
  const tipoTitle = (inv.tipo || "factura").toUpperCase();
  const isProforma = inv.tipo === "proforma";

  return (
    <div className="min-h-screen bg-white text-black print:bg-white">
      {/* Header (no se imprime) */}
      <div className="print:hidden bg-gray-100 border-b border-gray-300 p-4 flex justify-between items-center">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-400 rounded hover:bg-white"
          data-testid="back-btn"
        >
          <ArrowLeftIcon size={14} /> Volver
        </button>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700 text-sm font-medium"
          data-testid="print-btn"
        >
          <PrinterIcon size={16} weight="bold" /> Imprimir / Guardar PDF
        </button>
      </div>

      {/* Documento imprimible */}
      <div className="max-w-3xl mx-auto p-8 print:p-6 print:max-w-full">
        {/* Encabezado: emisor + tipo */}
        <div className="flex justify-between items-start border-b-2 border-gray-800 pb-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{company.nombre || company.razon_social}</h1>
            <div className="text-xs text-gray-700 mt-1 space-y-0.5">
              {company.rtn && <div>RTN: {company.rtn}</div>}
              {company.direccion && <div>{company.direccion}</div>}
              {company.telefono && <div>Tel: {company.telefono}</div>}
              {company.email && <div>Email: {company.email}</div>}
            </div>
          </div>
          <div className="text-right">
            <div className={`inline-block px-3 py-1 text-xs font-bold border-2 ${isProforma ? 'border-amber-600 text-amber-700 bg-amber-50' : 'border-emerald-700 text-emerald-700 bg-emerald-50'}`}>
              {tipoTitle}{isProforma ? " — NO ES FACTURA" : ""}
            </div>
            <div className="font-mono text-lg font-bold mt-2">{inv.numero}</div>
            <div className="text-xs text-gray-600 mt-1">Fecha: {fmtDate(inv.fecha)}</div>
            {inv.fecha_vencimiento && (
              <div className="text-xs text-gray-600">Vence: {fmtDate(inv.fecha_vencimiento)}</div>
            )}
          </div>
        </div>

        {/* CAI box (solo facturas) */}
        {!isProforma && (
          <div className="border border-gray-400 p-3 mb-4 text-xs">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-gray-500 uppercase text-[9px]">CAI</div>
                <div className="font-mono font-bold">{inv.cai}</div>
              </div>
              <div>
                <div className="text-gray-500 uppercase text-[9px]">Forma de pago</div>
                <div className="font-medium uppercase">{inv.forma_pago}</div>
              </div>
            </div>
          </div>
        )}

        {/* Cliente */}
        <div className="border border-gray-300 p-3 mb-4">
          <div className="text-[9px] uppercase text-gray-500 mb-1">Cliente</div>
          <div className="font-semibold text-sm">{cliente.nombre || inv.cliente_nombre}</div>
          <div className="text-xs text-gray-700">RTN: {cliente.rtn || inv.cliente_rtn || "—"}</div>
          {cliente.direccion && <div className="text-xs text-gray-700">{cliente.direccion}</div>}
          {cliente.telefono && <div className="text-xs text-gray-700">Tel: {cliente.telefono}</div>}
          <div className="grid grid-cols-3 gap-3 mt-2 text-xs">
            <div><span className="text-gray-500">Vendedor:</span> {inv.vendedor || "—"}</div>
            <div><span className="text-gray-500">Región:</span> {inv.region || "—"}</div>
            <div><span className="text-gray-500">Tipo venta:</span> {inv.tipo_venta || "directa"}</div>
          </div>
        </div>

        {/* Datos fiscales extra */}
        {(inv.numero_orden_compra_exenta || inv.numero_constancia_exonerado || inv.numero_registro_sag) && (
          <div className="border border-gray-300 p-3 mb-4 text-xs">
            <div className="text-[9px] uppercase text-gray-500 mb-1">Datos fiscales</div>
            <div className="grid grid-cols-2 gap-2">
              {inv.numero_orden_compra_exenta && <div>N° OC exenta: <strong>{inv.numero_orden_compra_exenta}</strong></div>}
              {inv.numero_constancia_exonerado && <div>N° constancia exonerado: <strong>{inv.numero_constancia_exonerado}</strong></div>}
              {inv.numero_registro_sag && <div>N° registro SAG: <strong>{inv.numero_registro_sag}</strong></div>}
            </div>
          </div>
        )}

        {/* Líneas */}
        <table className="w-full mb-4 border border-gray-300">
          <thead className="bg-gray-100">
            <tr className="text-left text-[10px] uppercase tracking-wider">
              <th className="p-2 border border-gray-300">Código</th>
              <th className="p-2 border border-gray-300">Descripción</th>
              <th className="p-2 border border-gray-300 text-right">Cant.</th>
              <th className="p-2 border border-gray-300 text-right">Precio</th>
              <th className="p-2 border border-gray-300 text-right">Subtotal</th>
              <th className="p-2 border border-gray-300 text-right">ISV</th>
              <th className="p-2 border border-gray-300 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {inv.items?.map((it, i) => (
              <tr key={i} className="border-b">
                <td className="p-2 border border-gray-300 font-mono text-xs">{it.codigo}</td>
                <td className="p-2 border border-gray-300 text-xs">{it.descripcion}</td>
                <td className="p-2 border border-gray-300 text-xs text-right">{it.cantidad}</td>
                <td className="p-2 border border-gray-300 text-xs text-right">{fmtL(it.precio_unitario)}</td>
                <td className="p-2 border border-gray-300 text-xs text-right">{fmtL(it.subtotal ?? ((it.cantidad || 0) * (it.precio_unitario || 0) - (it.descuento || 0)))}</td>
                <td className="p-2 border border-gray-300 text-xs text-right">{it.isv_pct}%</td>
                <td className="p-2 border border-gray-300 text-xs text-right font-medium">{fmtL(it.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totales */}
        <div className="flex justify-end">
          <div className="w-full max-w-xs text-xs space-y-1">
            <Row l="Subtotal" v={fmtL((inv.subtotal_gravado_15 || 0) + (inv.subtotal_gravado_18 || 0) + (inv.subtotal_exento || 0) + (inv.subtotal_exonerado || 0))} />
            {inv.subtotal_gravado_15 > 0 && <Row l="Gravado 15%" v={fmtL(inv.subtotal_gravado_15)} />}
            {inv.subtotal_gravado_18 > 0 && <Row l="Gravado 18%" v={fmtL(inv.subtotal_gravado_18)} />}
            {inv.subtotal_exento > 0 && <Row l="Exento" v={fmtL(inv.subtotal_exento)} />}
            {inv.subtotal_exonerado > 0 && <Row l="Exonerado" v={fmtL(inv.subtotal_exonerado)} />}
            {inv.descuento_total > 0 && <Row l="Descuento" v={`-${fmtL(inv.descuento_total)}`} />}
            {inv.isv_15 > 0 && <Row l="ISV 15%" v={fmtL(inv.isv_15)} />}
            {inv.isv_18 > 0 && <Row l="ISV 18%" v={fmtL(inv.isv_18)} />}
            <div className="border-t-2 border-gray-800 pt-2 mt-2 flex justify-between items-baseline">
              <span className="font-bold uppercase">TOTAL A PAGAR</span>
              <span className="font-bold text-lg">{fmtL(inv.total)}</span>
            </div>
            {inv.saldo_pendiente > 0 && <Row l="Saldo pendiente" v={fmtL(inv.saldo_pendiente)} bold />}
          </div>
        </div>

        {inv.codigo_promocional && (
          <div className="mt-4 border border-amber-300 bg-amber-50 p-2 text-xs">
            <strong>Código promocional aplicado:</strong> <span className="font-mono">{inv.codigo_promocional}</span>
          </div>
        )}
        {inv.dependiente_nombre && (
          <div className="mt-2 text-xs text-gray-700">
            <strong>Dependiente de tienda:</strong> {inv.dependiente_nombre}
            {inv.dependiente_puesto && <span className="text-gray-500"> · {inv.dependiente_puesto}</span>}
          </div>
        )}
        {/* Observaciones internas: solo en la proforma; la factura impresa no las muestra (siguen en el sistema) */}
        {isProforma && inv.notas && (
          <div className="mt-4 border-t pt-3 text-xs text-gray-700">
            <strong>Notas:</strong> {inv.notas}
          </div>
        )}
        {isProforma && inv.notas_proforma && (
          <div className="mt-2 border-l-4 border-amber-300 bg-amber-50/50 pl-3 py-2 text-xs text-gray-700">
            <strong>Notas de la proforma origen:</strong> {inv.notas_proforma}
          </div>
        )}

        {/* Pie */}
        <div className="mt-8 pt-4 border-t-2 border-gray-300 text-[10px] text-gray-500 text-center">
          {isProforma
            ? "Este documento es una PROFORMA. No tiene validez fiscal hasta ser convertida en factura."
            : `Documento autorizado por la SAR · CAI ${inv.cai} · Original cliente / Copia emisor`}
        </div>
      </div>

      <style>{`
        @media print {
          body { margin: 0; }
          @page { size: A4; margin: 12mm; }
        }
      `}</style>
    </div>
  );
}

function Row({ l, v, bold }) {
  return (
    <div className="flex justify-between">
      <span className={bold ? "font-bold" : "text-gray-600"}>{l}</span>
      <span className={bold ? "font-bold" : ""}>{v}</span>
    </div>
  );
}
